// Quick Audit ("one-click audit") shared logic.
// Detects failing Core Web Vitals per device from CrUX data, runs the deep
// lab diagnostic for the picked metric, fetches PSI diagnostics + optional AI
// narrative and produces a downloadable audit HTML report.

import { generateAuditHTML, downloadHTMLFile } from './diagnosticsExportStyles.js';
import { secureFetch } from '../services/api.js';
import { generateClsTasks } from './clsTasks.js';
import { deriveCanonicalFinding } from './canonicalFinding.js';
import { METRIC_THRESHOLDS } from './metricThresholds.js';

export { downloadHTMLFile, generateClsTasks };

export const CORE_VITALS_THRESHOLDS = {
  LCP: [METRIC_THRESHOLDS.lcp.good, METRIC_THRESHOLDS.lcp.poor],
  INP: [METRIC_THRESHOLDS.inp.good, METRIC_THRESHOLDS.inp.poor],
  CLS: [METRIC_THRESHOLDS.cls.good, METRIC_THRESHOLDS.cls.poor]
};

const CORE_METRICS = ['LCP', 'INP', 'CLS'];
const DEVICES = ['PHONE', 'DESKTOP', 'TABLET'];

const DEEP_ENDPOINTS = {
  LCP: '/api/lab-audits/lcp-diagnostics',
  INP: '/api/lab-audits/inp-diagnostics',
  CLS: '/api/lab-audits/cls-diagnostics',
  TTFB: '/api/lab-audits/ttfb-diagnostics'
};

export const getVitalsStatus = (metric, value) => {
  const thresholds = CORE_VITALS_THRESHOLDS[metric];
  if (!thresholds || value === null || value === undefined) return 'unknown';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return 'unknown';
  if (num <= thresholds[0]) return 'good';
  if (num <= thresholds[1]) return 'needs-improvement';
  return 'poor';
};

const extractP75 = (metricData) => {
  if (!metricData) return null;
  return metricData.value ?? metricData.p75 ?? metricData.percentiles?.p75 ?? null;
};

const fetchLcpFieldEvidence = async ({ entryUrl, analyzedUrl, fieldValue, device, origin }) => {
  if (!analyzedUrl) return null;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const res = await secureFetch('/api/crux/comprehensive', {
      method: 'POST',
      body: JSON.stringify({ url: analyzedUrl, useCache: false }),
      signal: controller.signal
    });
    const data = await res.json().catch(() => ({}));
    const factor = device === 'desktop' || device === 'DESKTOP'
      ? 'DESKTOP'
      : device === 'tablet' || device === 'TABLET' ? 'TABLET' : 'PHONE';
    const pageP75 = extractP75(data?.byFormFactor?.[factor]?.metrics?.LCP);
    const normalizeExactUrl = value => {
      try {
        const parsed = new URL(value);
        parsed.hash = '';
        parsed.pathname = parsed.pathname.replace(/\/+$/, '') || '/';
        return parsed.toString();
      } catch {
        return String(value || '').replace(/\/+$/, '');
      }
    };
    const exactPage = data?.queryMode === 'url'
      && normalizeExactUrl(data?.usedTarget) === normalizeExactUrl(analyzedUrl);
    return {
      source: 'crux',
      device,
      authoritative: {
        scope: 'origin',
        target: origin || null,
        p75: fieldValue ?? null,
        collectionPeriod: data?.collectionPeriod || null
      },
      entryPage: entryUrl === analyzedUrl ? {
        available: exactPage && pageP75 != null,
        requestedUrl: entryUrl,
        resolvedUrl: data?.usedTarget || null,
        formFactor: factor,
        p75: exactPage ? pageP75 : null,
        collectionPeriod: data?.byFormFactor?.[factor]?.collectionPeriod || null
      } : null,
      analyzedPage: {
        available: exactPage && pageP75 != null,
        requestedUrl: analyzedUrl,
        resolvedUrl: data?.usedTarget || null,
        formFactor: factor,
        p75: exactPage ? pageP75 : null,
        collectionPeriod: data?.byFormFactor?.[factor]?.collectionPeriod || null
      }
    };
  } catch (error) {
    return {
      source: 'crux',
      device,
      authoritative: { scope: 'origin', target: origin || null, p75: fieldValue ?? null },
      entryPage: null,
      analyzedPage: {
        available: false,
        requestedUrl: analyzedUrl,
        resolvedUrl: null,
        p75: null,
        error: error.message
      }
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

// Returns [{ metric, device, value, status }] for every failing
// (needs-improvement or poor) core vital per device. Falls back to the
// aggregated ALL form factor when no per-device data exists.
export const detectFailingVitals = (cruxData) => {
  const byFormFactor = cruxData?.byFormFactor;
  if (!byFormFactor) return [];

  const collect = (devices) => {
    const rows = [];
    for (const device of devices) {
      const deviceData = byFormFactor[device];
      if (!deviceData?.metrics || deviceData.error) continue;
      for (const metric of CORE_METRICS) {
        const value = extractP75(deviceData.metrics[metric]);
        if (value === null || value === undefined) continue;
        const status = getVitalsStatus(metric, value);
        if (status === 'needs-improvement' || status === 'poor') {
          rows.push({ metric, device, value, status });
        }
      }
    }
    return rows;
  };

  const perDevice = collect(DEVICES);
  if (perDevice.length > 0) return perDevice;
  return collect(['ALL']);
};

export const getTierSlug = (user) => {
  if (!user) return null;
  return typeof user.tier === 'string' ? user.tier : (user.tier?.slug || user.tier?.name || null);
};

// Tier feature flag if present (admin-configurable per tier), otherwise
// the legacy hard-coded slug list — mirrors the server-side gates.
const tierFlag = (user, key) =>
  (user && typeof user.tier === 'object') ? user.tier?.[key] : undefined;

export const hasQuickAuditAccess = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  const flag = tierFlag(user, 'allowQuickAudit');
  if (flag === true || flag === false) return flag;
  return ['pro', 'business', 'premium', 'enterprise'].includes(getTierSlug(user));
};

export const hasDeepTestAccess = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  const flag = tierFlag(user, 'allowDeepAudit');
  if (flag === true || flag === false) return flag;
  return ['premium', 'enterprise'].includes(getTierSlug(user));
};

// Never let a derivation bug kill the narrative fetch — the guardrail's safe
// fallback is "no canonical finding" (AI then must not name any cause).
const safeCanonicalFinding = (payload) => {
  try { return deriveCanonicalFinding(payload); } catch { return null; }
};

const fetchAiNarrative = async (user, payload) => {
  if (!user) return null;
  const aiFlag = tierFlag(user, 'allowAiNarrative');
  if (aiFlag === false && !user.is_admin) return null;
  if (aiFlag !== true && !user.is_admin) {
    const slug = getTierSlug(user);
    const ELIGIBLE = new Set(['business', 'enterprise']);
    if (slug && !ELIGIBLE.has(slug)) return null;
  }
  try {
    // secureFetch attaches CSRF token + Authorization Bearer (iframe-safe) automatically
    const res = await secureFetch('/api/exports/ai-narrative', {
      method: 'POST',
      body: JSON.stringify({
        variant: 'audit',
        // The narrative explains the MAIN deep findings — when the deep story
        // was promoted to the sweep's worst page, the narrative must be
        // anchored to that page, not to the entry URL.
        url: payload.deepAnalyzedUrl || payload.url,
        metric: payload.metric,
        value: payload.value,
        language: payload.language,
        device: payload.device,
        diagnosticsSummary: {
          culpritsCount: Array.isArray(payload.diagnostics?.culprits) ? payload.diagnostics.culprits.length : 0,
          recommendationsCount: payload.savingsRecommendations?.length || 0,
          multiPage: false,
          hasDeepResults: !!(payload.deepLCPResults || payload.deepCLSResults || payload.deepINPResults),
          // Headline savings = largest single opportunity (maxPotentialSavingsMs),
          // never the additive sum — opportunities overlap and summing them
          // inflates the estimate several-fold (e.g. 750 → 3870 ms).
          estimatedSavingsMs: payload.savingsSummary?.maxPotentialSavingsMs
            ?? payload.savingsSummary?.totalMs ?? null
        },
        // Causality guardrail: deterministic conclusion the AI must interpret
        // without introducing or strengthening a cause.
        canonicalFinding: safeCanonicalFinding(payload)
      })
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.success ? json.narrative : null;
  } catch (err) {
    console.warn('AI narrative fetch failed (silent fallback):', err?.message);
    return null;
  }
};

// PSI can transiently fail (500/503) — a missing diagnostics payload guts
// the report (no TBT, no culprits, generic fallback story), so retry twice
// before giving up and let the caller surface the failure.
// fetcher/delayFn are injectable for tests.
export const fetchDiagnosticsWithRetry = async (url, strategy, {
  fetcher = secureFetch,
  delayFn = (ms) => new Promise(r => setTimeout(r, ms)),
  maxAttempts = 3
} = {}) => {
  let diagnostics = null;
  let savingsRecommendations = [];
  let savingsSummary = null;
  for (let attempt = 1; attempt <= maxAttempts && !diagnostics; attempt++) {
    try {
      const res = await fetcher('/api/compare/diagnostics', {
        method: 'POST',
        body: JSON.stringify({ url, strategy })
      });
      if (res.ok) {
        const data = await res.json();
        diagnostics = data.diagnostics || null;
        savingsRecommendations = Array.isArray(data.recommendations) ? data.recommendations : [];
        savingsSummary = data.recommendationsSummary || null;
      } else {
        console.warn(`Quick audit diagnostics attempt ${attempt}/${maxAttempts} failed: HTTP ${res.status}`);
      }
    } catch (err) {
      console.error(`Quick audit diagnostics attempt ${attempt}/${maxAttempts} error:`, err);
    }
    if (!diagnostics && attempt < maxAttempts) {
      await delayFn(2000 * attempt);
    }
  }
  return { diagnostics, savingsRecommendations, savingsSummary };
};

// Reachability preflight — asks the server (~8s max) whether the target site
// responds to HTTP at all BEFORE starting the multi-minute deep-audit cascade.
// Throws a user-facing localized error when the site is unreachable; fails
// OPEN (returns silently) when the preflight endpoint itself errors, so an
// internal hiccup never blocks a legitimate audit. fetcher is injectable for tests.
export const checkSiteReachability = async (url, language, { fetcher = secureFetch, signal = null } = {}) => {
  let json = null;
  try {
    const res = await fetcher('/api/lab-audits/reachability', {
      method: 'POST',
      body: JSON.stringify({ url }),
      signal
    });
    if (!res.ok) return; // fail open
    json = await res.json();
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    console.warn('Reachability preflight failed (continuing):', err?.message);
    return; // fail open
  }
  if (json?.success && json.reachable === false) {
    const e = new Error(language === 'cs'
      ? 'Cílový web neodpovídá — je nedostupný, nebo blokuje automatizované měření. Audit se proto nespustil. Zkontrolujte adresu a zkuste to později.'
      : 'The target site is not responding — it is unavailable or blocks automated measurement. The audit was not started. Check the address and try again later.');
    e.code = 'SITE_UNREACHABLE';
    throw e;
  }
};

const buildAuditFilename = (url, metric) => {
  let domain = 'report';
  if (url) {
    try {
      const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      domain = new URL(normalized).hostname.replace(/\./g, '_');
    } catch (_) {
      domain = String(url).replace(/[^a-z0-9]+/gi, '_').slice(0, 60) || 'report';
    }
  }
  return `audit_${metric}_${domain}_${new Date().toISOString().split('T')[0]}_${Date.now()}.html`;
};

// =========================================================================
// SERVER-SIDE COMPLEX AUDIT (queue-backed) — the browser only starts the
// job and polls progress; the orchestration runs in the server queue worker
// (server/services/complexAudit.js) and SURVIVES closing the tab. The
// finished report is stored server-side as a saved analysis.
// =========================================================================
export const canonicalAuditUrl = (value) => {
  try {
    const parsed = new URL(value);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    parsed.hash = '';
    return parsed.href;
  } catch {
    return null;
  }
};

export const auditUrlsMatch = (left, right) => {
  const leftCanonical = canonicalAuditUrl(left);
  const rightCanonical = canonicalAuditUrl(right);
  return !!leftCanonical && !!rightCanonical && leftCanonical === rightCanonical;
};

const ACTIVE_COMPLEX_STATUSES = new Set(['pending', 'running', 'awaiting_review']);

const conflictStatusLabel = (status, language) => {
  const labels = language === 'cs'
    ? { pending: 'čeká ve frontě', running: 'probíhá', awaiting_review: 'čeká na schválení' }
    : { pending: 'queued', running: 'running', awaiting_review: 'awaiting review' };
  return labels[status] || status || (language === 'cs' ? 'aktivní' : 'active');
};

export const activeAuditConflictMessage = ({ activeUrl, activeStatus, language = 'cs' }) => {
  const blockingUrl = activeUrl || (language === 'cs' ? 'jiný web' : 'another website');
  const status = conflictStatusLabel(activeStatus, language);
  if (activeStatus === 'awaiting_review') {
    return language === 'cs'
      ? `Komplexní audit pro ${blockingUrl} čeká na schválení administrátorem. Nový audit tohoto webu nebyl spuštěn. Nejprve musí administrátor vyřešit předchozí report.`
      : `The complex audit for ${blockingUrl} is awaiting administrator review. No new audit was started for this website. The previous report must be reviewed first.`;
  }
  return language === 'cs'
    ? `Komplexní audit pro ${blockingUrl} je již aktivní (stav: ${status}). Dokončete nebo zrušte jej v Moje analýzy, než spustíte audit tohoto webu.`
    : `A complex audit for ${blockingUrl} is already active (status: ${status}). Finish or cancel it in My analyses before starting an audit for this website.`;
};

const verifiedConflictRow = (row, requestedUrl) => {
  const candidate = row?.analysis || row;
  if (!candidate
    || (candidate.analysis_type && candidate.analysis_type !== 'complex-audit')
    || !ACTIVE_COMPLEX_STATUSES.has(candidate.status)
    || !auditUrlsMatch(candidate.url, requestedUrl)) return null;
  return candidate;
};

export const startComplexAuditServer = async (
  { url, skipCmp = true, failingVitals = [], language = 'cs' },
  { fetcher = secureFetch } = {}
) => {
  const res = await fetcher('/api/lab-audits/complex-audit', {
    method: 'POST',
    body: JSON.stringify({
      url,
      skipCmp,
      language,
      failingVitals: (failingVitals || []).map(v => ({ metric: v.metric, device: v.device, value: v.value }))
    })
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    let verifiedQueueId = null;
    let activeUrl = json.activeUrl;
    let activeStatus = json.activeStatus;
    if (res.status === 409 && json.queueId) {
      if (json.canReattach === true && auditUrlsMatch(json.activeUrl, url)
        && ACTIVE_COMPLEX_STATUSES.has(json.activeStatus)) {
        verifiedQueueId = json.queueId;
      } else if (json.activeUrl == null || json.activeStatus == null || json.canReattach == null) {
        // Compatibility with an older server response. Never trust queueId
        // alone: fetch the owned queue row and verify URL/type/status first.
        try {
          const rowRes = await fetcher(`/api/auth/analysis/${json.queueId}`);
          const rowJson = rowRes.ok ? await rowRes.json().catch(() => null) : null;
          const row = verifiedConflictRow(rowJson, url);
          if (row) {
            verifiedQueueId = json.queueId;
            activeUrl = row.url;
            activeStatus = row.status;
          } else {
            const candidate = rowJson?.analysis || rowJson;
            activeUrl = candidate?.url || activeUrl;
            activeStatus = candidate?.status || activeStatus;
          }
        } catch {
          // Missing/unavailable metadata is not safe grounds for attachment.
        }
      }
    }
    const isDifferentActiveAudit = res.status === 409 && !verifiedQueueId;
    const startupMessage = language === 'cs'
      ? 'Auditní služba se právě připravuje. Zkuste audit znovu za chvíli.'
      : 'The audit service is still starting. Please try again shortly.';
    const e = new Error(isDifferentActiveAudit
      ? activeAuditConflictMessage({ activeUrl, activeStatus, language })
      : (json.code === 'ANALYSIS_QUEUE_INITIALIZING'
          ? startupMessage
          : (json.error || (language === 'cs' ? 'Spuštění auditu selhalo.' : 'Starting the audit failed.'))));
    e.status = res.status;
    e.code = json.code || (res.status === 409 ? 'ACTIVE_COMPLEX_AUDIT_CONFLICT' : undefined);
    e.activeUrl = activeUrl || null;
    e.activeStatus = activeStatus || null;
    e.retryable = json.retryable === true;
    if (verifiedQueueId) e.queueId = verifiedQueueId;
    throw e;
  }
  return json; // { queueId, position, estimatedWaitSeconds }
};

export const getComplexAuditStatus = async (queueId) => {
  const res = await secureFetch(`/api/auth/analysis/${queueId}`);
  if (!res.ok) return null;
  return res.json().catch(() => null);
};

export const findRunningComplexAudit = async (url) => {
  const res = await secureFetch('/api/auth/my-analyses');
  if (!res.ok) throw new Error('Could not load running audits');
  const { analyses } = await res.json();
  return (analyses || []).find(row =>
    row.analysis_type === 'complex-audit'
    && ACTIVE_COMPLEX_STATUSES.has(row.status)
    && auditUrlsMatch(row.url, url)
  ) || null;
};

export const cancelComplexAudit = async (queueId) => {
  const res = await secureFetch(`/api/auth/analysis/${queueId}/cancel`, { method: 'POST', body: JSON.stringify({}) });
  return res.ok;
};

// Downloads the server-stored audit report HTML (saved-analysis, variant
// 'audit') and hands it to the browser download helper.
export const downloadStoredAuditReport = async (labAuditId, filename) => {
  const res = await secureFetch(`/api/lab-audits/saved-analysis/${labAuditId}/html?variant=audit`);
  if (!res.ok) throw new Error('Report download failed');
  const html = await res.text();
  downloadHTMLFile(html, filename || `audit_CWV_${labAuditId}.html`);
};

// =========================================================================
// COMPLETE CWV AUDIT (unified) — the default report.
// One run: device-priority ranking across all metrics + deep layer for every
// above-threshold metric that has one (INP, CLS, LCP today; TTFB/FCP get a
// prepared "depth pending" slot in the report until their layers exist).
// =========================================================================
// Deep-layer registry: metric -> endpoint. Adding a new metric's deep layer
// later = add it here (and its sections in the export); the orchestration
// and report structure stay unchanged.
const DEEP_LAYER_ENDPOINTS = {
  INP: '/api/lab-audits/inp-diagnostics',
  CLS: '/api/lab-audits/cls-diagnostics',
  LCP: '/api/lab-audits/lcp-diagnostics',
  TTFB: '/api/lab-audits/ttfb-diagnostics'
};

const DP_DEVICE_TO_STRATEGY = { phone: 'mobile', desktop: 'desktop', tablet: 'tablet' };

// onStage receives ({ stage, metric?, index?, total? }) so the UI can show
// per-metric progress. Returns { html, filename, ranMetrics, failedMetrics,
// pendingMetrics, diagnosticsFailed }.
export const runComplexAudit = async ({ url, user, language, onStage, skipCmp = true, autoDownload = true, failingVitals = [], signal = null }) => {
  const deepAllowed = hasDeepTestAccess(user);
  const throwIfAborted = () => {
    if (signal?.aborted) {
      const e = new Error(language === 'cs' ? 'Audit zrušen.' : 'Audit cancelled.');
      e.name = 'AbortError';
      throw e;
    }
  };

  // 0) Reachability preflight (~8s max) — a dead/blocking site must fail HERE
  //    with a clear message, not after ~10 minutes of deep-run timeouts.
  onStage?.({ stage: 'priority' });
  await checkSiteReachability(url, language, { signal });
  throwIfAborted();

  // 1) Priority ranking first — it decides the primary device, the metric
  //    order, and which metrics get their deep layer in this run.
  let devicePriority = null;
  try {
    const dpRes = await secureFetch('/api/lab-audits/device-priority', {
      method: 'POST',
      body: JSON.stringify({ url }),
      signal
    });
    const dpJson = await dpRes.json().catch(() => ({}));
    if (dpRes.ok && dpJson.success) devicePriority = dpJson.data;
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    console.warn('Complex audit device-priority fetch failed:', err);
  }
  throwIfAborted();

  // Actionable = above threshold, in the server's rank order (ties included).
  const actionable = (devicePriority?.items || []).filter(i => Number(i.score) > 0);
  // Fallback when CrUX has no data: derive from the failing vitals the caller
  // already detected (modal rows), keeping their order. Unknown/ALL form
  // factors default to phone — mobile is the default measurement device.
  const fallback = actionable.length ? [] : failingVitals.map(v => ({
    metric: String(v.metric || '').toLowerCase(),
    device: v.device === 'DESKTOP' ? 'desktop' : v.device === 'TABLET' ? 'tablet' : 'phone',
    p75: v.value,
    score: 1
  }));
  const ranked = actionable.length ? actionable : fallback;
  if (!ranked.length) {
    throw new Error(language === 'cs'
      ? 'Žádná metrika není nad prahem — není co hloubkově auditovat.'
      : 'No metric is above threshold — nothing to deep-audit.');
  }

  // Primary device = the ranking's #1 device; every deep run measures on it
  // so the report is internally consistent (one device, one story).
  const primary = ranked[0];
  const deviceStrategy = DP_DEVICE_TO_STRATEGY[primary.device] || 'mobile';

  // Unique metrics in rank order.
  const metricsInOrder = [...new Set(ranked.map(i => String(i.metric).toUpperCase()))];

  // 2) Deep layer for every above-threshold (metric, device) pair that has
  //    one — sequentially, one run after another. The primary-device run of
  //    each metric feeds the report's main deep sections; runs on other
  //    devices are collected into deviceRuns for the device-comparison view.
  const deep = { INP: null, CLS: null, LCP: null, TTFB: null };
  const deviceRuns = [];
  const ranMetrics = [];
  const failedMetrics = [];
  const deepFailures = {};
  const pendingMetrics = metricsInOrder.filter(m => !DEEP_LAYER_ENDPOINTS[m]);
  const runDeepRequest = async (m, strategy, targetUrl = url) => {
    // Per-request timeout combined with the caller's cancel signal (modal
    // close aborts the in-flight deep request, not just the next step).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000);
    const onParentAbort = () => controller.abort();
    signal?.addEventListener('abort', onParentAbort);
    try {
      const res = await secureFetch(DEEP_LAYER_ENDPOINTS[m], {
        method: 'POST',
        // INP's routes speak explicit cmpMode (its multi-page sweep sends
        // cmpMode too) — send the same canonical value so a deep run and its
        // sweep can never diverge on consent state. skipCmp stays for the
        // other metrics' routes.
        body: JSON.stringify(m === 'INP'
          ? { url: targetUrl, skipCmp, cmpMode: skipCmp ? 'accept' : 'none', device: strategy }
          : { url: targetUrl, skipCmp, device: strategy }),
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success && data.data) {
        if (targetUrl === url) delete deepFailures[m];
        return data.data;
      }
      // The server owns failure classification. Preserve its contract for the
      // legacy client-side export path; never recreate types from error text.
      if (targetUrl === url && data?.data?.failure) {
        deepFailures[m] = data.data.failure;
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onParentAbort);
    }
  };
  if (deepAllowed) {
    // (metric, device) pairs above threshold, deduped, rank-stable: pairs
    // run STRICTLY from actionable ranked items — never synthesize a run on
    // the overall primary device for a metric that is green there (that
    // would invent a pair and mislabel another device's field p75 as the
    // primary device's). Each metric's FIRST ranked pair (its highest
    // share×gap device) feeds deep[m] and is that metric's primary run.
    const seen = new Set();
    const pairs = [];
    for (const item of ranked) {
      const m = String(item.metric).toUpperCase();
      if (!DEEP_LAYER_ENDPOINTS[m]) continue;
      const strategy = DP_DEVICE_TO_STRATEGY[item.device] || 'mobile';
      const key = `${m}:${strategy}`;
      if (!seen.has(key)) {
        const isFirstForMetric = !pairs.some(p => p.metric === m);
        seen.add(key);
        pairs.push({ metric: m, device: item.device, strategy, p75: item.p75 ?? null, share: item.deviceShare ?? null, threshold: item.threshold ?? null, primary: isFirstForMetric });
      }
    }
    for (let i = 0; i < pairs.length; i++) {
      const { metric: m, device, strategy, p75, share, threshold, primary: isPrimary } = pairs[i];
      throwIfAborted();
      onStage?.({ stage: 'deep', metric: m, device, index: i + 1, total: pairs.length });
      const base = { metric: m, device, strategy, fieldP75: p75, deviceShare: share, threshold, primary: isPrimary };
      try {
        const result = await runDeepRequest(m, strategy);
        if (result) {
          if (isPrimary && !deep[m]) {
            deep[m] = result;
            if (!ranMetrics.includes(m)) ranMetrics.push(m);
          }
          deviceRuns.push({
            ...base,
            value: m === 'CLS' ? (result.cls ?? null) : m === 'INP' ? (result.inp ?? null) : m === 'TTFB' ? (result.ttfb ?? null) : (result.lcp ?? null),
            success: true,
            // Full result so the report can name the culprit PER DEVICE —
            // a worse minority device needs its cause, not just its number.
            result
          });
        } else if (strategy === deviceStrategy) {
          if (!failedMetrics.includes(m)) failedMetrics.push(m);
          deviceRuns.push({ ...base, value: null, success: false });
        } else {
          deviceRuns.push({ ...base, value: null, success: false });
        }
      } catch (err) {
        throwIfAborted();
        console.error(`Complex audit deep ${m} (${strategy}) error:`, err);
        if (strategy === deviceStrategy && !failedMetrics.includes(m)) failedMetrics.push(m);
        deviceRuns.push({ ...base, value: null, success: false });
      }
    }
  }
  throwIfAborted();

  // 2b) Multi-page sweep for every above-threshold metric with a multi-page
  //     endpoint — the deep story must not rest on the homepage alone.
  const multiPage = { CLS: null, INP: null, LCP: null, TTFB: null };
  if (deepAllowed) {
    const MP_MAX_PAGES = 6;
    const mpMetrics = metricsInOrder.filter(m => ['CLS', 'INP', 'LCP', 'TTFB'].includes(m));
    for (let i = 0; i < mpMetrics.length; i++) {
      const m = mpMetrics[i];
      throwIfAborted();
      onStage?.({ stage: 'multipage', metric: m, index: i + 1, total: mpMetrics.length });
      try {
        if (m === 'CLS') {
          // Async job with polling.
          const res = await secureFetch('/api/lab-audits/multi-page-cls', {
            method: 'POST',
            // Same method as the deep run: same device and same consent state
            // (skipCmp=true = auto-accept common CMPs on every page).
            body: JSON.stringify({ url, maxPages: MP_MAX_PAGES, skipCmp, device: deviceStrategy }),
            signal
          });
          const data = await res.json();
          if (data.success && data.async && data.jobId) {
            const deadline = Date.now() + 480000; // 8 min cap
            // Abort-aware sleep: modal close stops polling immediately (the
            // queued server job itself finishes in the background — same as
            // the manual multi-page button in the diagnostics panel).
            const sleep = (ms) => new Promise((resolve, reject) => {
              const t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
              const onAbort = () => { clearTimeout(t); reject(Object.assign(new Error('aborted'), { name: 'AbortError' })); };
              signal?.addEventListener('abort', onAbort, { once: true });
              if (signal?.aborted) onAbort();
            });
            while (Date.now() < deadline) {
              throwIfAborted();
              await sleep(3000);
              const sRes = await secureFetch(`/api/lab-audits/multi-page-cls/status/${data.jobId}`, { signal });
              const sData = await sRes.json().catch(() => ({}));
              if (sData.status === 'completed' && sData.data) { multiPage.CLS = sData.data; break; }
              if (sData.status === 'failed') break;
            }
          } else if (data.success && data.data) {
            multiPage.CLS = data.data;
          }
        } else if (m === 'TTFB') {
          // TTFB is measured on the first navigation, before any CMP
          // interaction — device-matched only. The route returns the results
          // object at the top level (no { data } envelope).
          const res = await secureFetch('/api/lab-audits/multi-page-ttfb', {
            method: 'POST',
            body: JSON.stringify({ url, maxPages: MP_MAX_PAGES, device: deviceStrategy }),
            signal
          });
          const data = await res.json();
          if (data.success) multiPage.TTFB = data.data ?? data;
        } else {
          const endpoint = m === 'INP' ? '/api/lab-audits/multi-page-inp' : '/api/lab-audits/multi-page-lcp';
          // Same method as the deep run: device-matched, consent-matched.
          const body = m === 'INP'
            ? { url, maxPages: MP_MAX_PAGES, cmpMode: skipCmp ? 'accept' : 'none', device: deviceStrategy }
            : { url, maxPages: MP_MAX_PAGES, skipCmp, device: deviceStrategy };
          const res = await secureFetch(endpoint, { method: 'POST', body: JSON.stringify(body), signal });
          const data = await res.json();
          if (data.success && data.data) multiPage[m] = data.data;
        }
      } catch (err) {
        throwIfAborted();
        console.error(`Complex audit multi-page ${m} error:`, err);
        // Non-fatal: the single-page deep story stands on its own.
      }
    }
  }
  throwIfAborted();

  // 2c) Sweep→deep: the sweep names the worst pages, but the deep layers so
  //     far ran on the entry URL only. Run the PRIMARY metric's deep layer on
  //     up to 2 worst above-threshold sweep pages (distinct from the entry
  //     URL) so the named culprit belongs to the page the sweep points at.
  //     Up to three pages, not one: the sweep is one shallow run per page, so
  //     a single "worst" can be noise — independent deep runs let the report
  //     compare culprits across pages (same culprit = template problem)
  //     instead of trusting one outlier.
  const NEEDS_ATTENTION = {
    cls: METRIC_THRESHOLDS.cls.good,
    inp: METRIC_THRESHOLDS.inp.good,
    lcp: METRIC_THRESHOLDS.lcp.good,
    ttfb: METRIC_THRESHOLDS.ttfb.good
  };
  const sweepDeepRuns = [];
  let sweepDeepSkipReason = null;
  if (deepAllowed) {
    const m = String(primary.metric).toUpperCase();
    const mKey = m.toLowerCase();
    const mp = multiPage[m];
    const mpPages = Array.isArray(mp?.pages) ? mp.pages
      : Array.isArray(mp?.pageResults) ? mp.pageResults : [];
    const normU = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
    const candidates = DEEP_LAYER_ENDPOINTS[m] ? mpPages
      .map(p => ({ url: p.url, value: Number(p[mKey]) }))
      .filter(p => p.url && Number.isFinite(p.value)
        && p.value > NEEDS_ATTENTION[mKey]
        && normU(p.url) !== normU(url))
      .sort((a, b) => b.value - a.value)
      .slice(0, 3) : [];
    if (!DEEP_LAYER_ENDPOINTS[m]) {
      sweepDeepSkipReason = 'no_deep_endpoint';
    } else if (!mpPages.length) {
      sweepDeepSkipReason = 'no_sweep_data';
    } else {
      const aboveThreshold = mpPages.filter(p => p.url && Number.isFinite(Number(p[mKey])) && Number(p[mKey]) > NEEDS_ATTENTION[mKey]);
      if (!aboveThreshold.length) {
        sweepDeepSkipReason = 'no_candidates_above_threshold';
      } else if (aboveThreshold.every(p => normU(p.url) === normU(url))) {
        sweepDeepSkipReason = 'entry_is_worst';
      }
    }
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      throwIfAborted();
      onStage?.({ stage: 'sweepdeep', metric: m, index: i + 1, total: candidates.length });
      try {
        const result = await runDeepRequest(m, deviceStrategy, c.url);
        sweepDeepRuns.push({ metric: m, url: c.url, sweepValue: c.value, result: result || null, success: !!result });
      } catch (err) {
        throwIfAborted();
        console.error(`Complex audit sweep-deep ${m} (${c.url}) error:`, err);
        sweepDeepRuns.push({ metric: m, url: c.url, sweepValue: c.value, result: null, success: false });
      }
    }
    if (sweepDeepRuns.length === 0 && sweepDeepSkipReason) {
      onStage?.({ stage: 'sweepdeep', status: 'skipped', skipReason: sweepDeepSkipReason });
    }
  } else {
    sweepDeepSkipReason = 'deep_not_allowed';
    onStage?.({ stage: 'sweepdeep', status: 'skipped', skipReason: sweepDeepSkipReason });
  }
  throwIfAborted();

  // 2d) Promote the worst page's deep run to the MAIN deep story. The sweep
  //     said the problem is strongest on a different page than the entry URL
  //     — the report's headline diagnostics and handover must come from that
  //     page, with the entry page kept as a reference. The DEEP value decides
  //     (not the sweep's single shallow run): the worst successful sweep-deep
  //     run is promoted only when it measures worse than the entry-page run.
  let deepAnalyzedUrl = url;
  let entryDeep = null;
  if (sweepDeepRuns.length) {
    const m = String(primary.metric).toUpperCase();
    const valOf = (res) => res == null ? null
      : m === 'CLS' ? (res.cls ?? null) : m === 'INP' ? (res.inp ?? null)
      : m === 'TTFB' ? (res.ttfb ?? null) : (res.lcp ?? null);
    // INP results are only comparable/promotable when the run captured a
    // valid attributed interaction — the same predicate the report's
    // scenario detection uses. A higher INP number from a run with failed
    // attribution must never replace a valid entry-page run.
    const inpValid = (res) => {
      if (!res) return false;
      const wi = res.worstInteraction;
      const total = (wi?.inputDelay || 0) + (wi?.processingTime || 0) + (wi?.presentationDelay || 0);
      return res.attributionValid !== false && !!wi && total > 0;
    };
    const ok = sweepDeepRuns.filter(s => s.success && valOf(s.result) != null
      && (m !== 'INP' || inpValid(s.result)));
    if (ok.length) {
      const worstRun = ok.reduce((w, s) => (valOf(s.result) > valOf(w.result) ? s : w));
      const entryVal = valOf(deep[m]);
      const worstVal = valOf(worstRun.result);
      // For INP an entry run without valid attribution loses to any valid
      // worst-page run; otherwise the worse deep value wins.
      const entryUsable = deep[m] != null && entryVal != null && (m !== 'INP' || inpValid(deep[m]));
      if (!entryUsable || worstVal > entryVal) {
        entryDeep = { url, metric: m, value: entryVal, result: deep[m] };
        deep[m] = worstRun.result;
        deepAnalyzedUrl = worstRun.url;
        worstRun.promoted = true;
        if (!ranMetrics.includes(m)) ranMetrics.push(m);
        const fi = failedMetrics.indexOf(m);
        if (fi !== -1) failedMetrics.splice(fi, 1);
        delete deepFailures[m];
      }
    }
  }

  // 3) PSI diagnostics on the primary device.
  onStage?.({ stage: 'diagnostics' });
  const psiStrategy = deviceStrategy === 'desktop' ? 'desktop' : 'mobile';
  const { diagnostics, savingsRecommendations, savingsSummary } =
    await fetchDiagnosticsWithRetry(url, psiStrategy);
  const diagnosticsFailed = !diagnostics;
  throwIfAborted();

  if (!diagnostics && !ranMetrics.length) {
    const err = new Error(language === 'cs'
      ? 'Cílový web se nepodařilo změřit — neodpověděl hloubkovému testu ani měření Google PageSpeed (vypršely časové limity). Web pravděpodobně blokuje automatizované měření, nebo byl dočasně nedostupný. Zkuste audit spustit později.'
      : 'The target site could not be measured — it responded neither to the deep lab test nor to Google PageSpeed (timeouts). The site likely blocks automated measurement or was temporarily unavailable. Try running the audit later.');
    err.code = 'SITE_UNREACHABLE';
    throw err;
  }

  // 4) Unified report: primary metric = priority #1; every measured deep
  //    layer is attached and renders presence-gated in the export.
  const primaryMetric = String(primary.metric).toUpperCase();
  const lcpFieldEvidence = primaryMetric === 'LCP' && deep.LCP
    ? await fetchLcpFieldEvidence({
        entryUrl: url,
        analyzedUrl: deepAnalyzedUrl,
        fieldValue: primary.p75,
        device: deviceStrategy,
        origin: devicePriority?.origin
      })
    : null;
  const payload = {
    url,
    metric: primaryMetric,
    value: primary.p75 ?? null,
    fieldValue: primary.p75 ?? null,
    unified: true,
    diagnostics,
    deepCLSResults: deep.CLS,
    deepINPResults: deep.INP,
    deepLCPResults: deep.LCP,
    deepTTFBResults: deep.TTFB,
    multiPageResults: multiPage.CLS,
    multiPageINPResults: multiPage.INP,
    multiPageLCPResults: multiPage.LCP,
    multiPageTTFBResults: multiPage.TTFB,
    sweepDeepRuns,
    sweepDeepSkipReason,
    // URL the primary metric's MAIN deep diagnostics actually ran on (the
    // sweep's worst page when promoted, else the entry URL) + the entry-page
    // run kept as reference.
    deepAnalyzedUrl,
    entryDeep,
    lcpFieldEvidence,
    deepDeviceRuns: deviceRuns,
    tasks: deep.CLS ? generateClsTasks(deep.CLS, language) : [],
    language,
    device: primary.device === 'phone' ? 'PHONE' : primary.device === 'tablet' ? 'TABLET' : 'DESKTOP',
    savingsRecommendations,
    savingsSummary,
    devicePriority,
    // Deep layers that FAILED (timeout/error) — the export must know, so a
    // failed priority-#1 deep layer produces an honest "analysis failed"
    // headline instead of silently falling back to another metric's story.
    failedDeepMetrics: failedMetrics.slice(),
    deepFailures
  };

  onStage?.({ stage: 'narrative' });
  const aiNarrative = await fetchAiNarrative(user, payload);

  onStage?.({ stage: 'generating' });
  const html = generateAuditHTML({ ...payload, aiNarrative });
  const filename = buildAuditFilename(url, 'CWV');
  if (autoDownload) downloadHTMLFile(html, filename);

  return {
    html,
    filename,
    ranMetrics,
    failedMetrics,
    deepFailures,
    pendingMetrics,
    diagnosticsFailed
  };
};

// Runs the full one-click audit pipeline for a single failing metric+device.
// Stages reported via onStage: 'deep' | 'diagnostics' | 'narrative' | 'generating'.
// skipCmp=true makes the deep lab test click "Accept all" on the cookie
// banner first (same toggle as the manual diagnostics panels).
// Returns { deepFailed, diagnosticsFailed, html, filename } — deepFailed=true
// when the deep lab test errored but the audit was still produced from PSI
// diagnostics; diagnosticsFailed=true when PSI diagnostics could not be
// fetched (report is limited to deep lab data only).
export const runQuickAudit = async ({ url, metric, device, fieldValue, user, language, onStage, skipCmp = false, autoDownload = true, auditContext = null }) => {
  if (!DEEP_ENDPOINTS[metric]) throw new Error('Unsupported diagnostic metric');
  const deepAllowed = hasDeepTestAccess(user);
  const strategy = device === 'PHONE' ? 'mobile' : 'desktop';
  const deepDevice = device === 'PHONE' ? 'mobile' : device === 'TABLET' ? 'tablet' : 'desktop';

  // Reachability preflight — same rationale as the unified flow: refuse to
  // start the deep test + PSI cascade against a site that doesn't answer HTTP.
  onStage?.('deep');
  await checkSiteReachability(url, language);

  let deep = null;
  let deepFailed = false;
  let deepFailure = null;
  if (deepAllowed) {
    onStage?.('deep');
    const controller = new AbortController();
    // Must exceed the server-side deep-analysis timeout (150s) with margin,
    // or the client gives up while the server would still have succeeded.
    const timeoutId = setTimeout(() => controller.abort(), 170000);
    try {
      // secureFetch attaches Authorization Bearer so premium deep access works
      // even inside the proxied preview iframe where session cookies get dropped
      const res = await secureFetch(DEEP_ENDPOINTS[metric], {
        method: 'POST',
        body: JSON.stringify({ url, skipCmp, device: deepDevice }),
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success && data.data) {
        deep = data.data;
      } else {
        deepFailed = true;
        deepFailure = data?.data?.failure || null;
      }
    } catch (err) {
      console.error(`Quick audit deep ${metric} test error:`, err);
      deepFailed = true;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  onStage?.('diagnostics');
  // CrUX field matrix (metric × device) — powers the executive-summary table
  // and the field-first loading verdict in the audit export. Fetched live at
  // run time, so it always reflects the current 28-day CrUX window. Optional:
  // a site without CrUX data still gets a (lab-only) audit.
  let devicePriority = null;
  try {
    const dpRes = await secureFetch('/api/lab-audits/device-priority', {
      method: 'POST',
      body: JSON.stringify({ url })
    });
    const dpJson = await dpRes.json().catch(() => ({}));
    if (dpRes.ok && dpJson.success) devicePriority = dpJson.data;
  } catch (err) {
    console.warn('Quick audit device-priority fetch failed (non-fatal):', err);
  }

  const { diagnostics, savingsRecommendations, savingsSummary } =
    await fetchDiagnosticsWithRetry(url, strategy);
  const diagnosticsFailed = !diagnostics;

  if (!deep && !diagnostics) {
    const err = new Error(language === 'cs'
      ? 'Cílový web se nepodařilo změřit — neodpověděl hloubkovému testu ani měření Google PageSpeed (vypršely časové limity). Web pravděpodobně blokuje automatizované měření, nebo byl dočasně nedostupný. Zkuste audit spustit později.'
      : 'The target site could not be measured — it responded neither to the deep lab test nor to Google PageSpeed (timeouts). The site likely blocks automated measurement or was temporarily unavailable. Try running the audit later.');
    err.code = 'SITE_UNREACHABLE';
    throw err;
  }

  const payload = {
    url,
    metric,
    auditContext,
    value: fieldValue ?? (metric === 'CLS' ? deep?.cls : null),
    fieldValue: fieldValue ?? null,
    diagnostics,
    deepCLSResults: metric === 'CLS' ? deep : null,
    deepINPResults: metric === 'INP' ? deep : null,
    deepLCPResults: metric === 'LCP' ? deep : null,
    deepTTFBResults: metric === 'TTFB' ? deep : null,
    deepAnalyzedUrl: url,
    lcpFieldEvidence: metric === 'LCP' && deep
      ? await fetchLcpFieldEvidence({
          entryUrl: url,
          analyzedUrl: url,
          fieldValue,
          device,
          origin: devicePriority?.origin
        })
      : null,
    multiPageResults: null,
    multiPageINPResults: null,
    multiPageLCPResults: null,
    tasks: metric === 'CLS' && deep ? generateClsTasks(deep, language) : [],
    language,
    device,
    savingsRecommendations,
    savingsSummary,
    devicePriority,
    // Same honesty rule as the unified flow: a failed deep layer of the
    // audited metric must yield the "analysis failed" scenario + banner,
    // never a fallback to a load-time/tracking narrative.
    failedDeepMetrics: deepFailed ? [metric] : [],
    deepFailures: deepFailure ? { [metric]: deepFailure } : {}
  };

  onStage?.('narrative');
  const aiNarrative = await fetchAiNarrative(user, payload);

  onStage?.('generating');
  const html = generateAuditHTML({ ...payload, aiNarrative });
  const filename = buildAuditFilename(url, metric);
  if (autoDownload) downloadHTMLFile(html, filename);

  return { deepFailed, diagnosticsFailed, html, filename };
};
